import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Tooltip,
  message
} from 'antd';
import {
  FilterOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faPlay,
  faBolt,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import VoiceCallService from '../../services/VoiceCallService';
import CallDetailsDrawer from './components/CallDetailsDrawer';
import RecordingPlayerModal from './components/RecordingPlayerModal';
import {
  PageContainer,
  PageContent,
  FiltersCard,
  FiltersBar,
  QuickFilterGroup,
  TableContainer,
  DirectionCell,
  PhoneNumberCell,
  DurationBadge,
  StatusBadge,
  RecordingCell,
  DateTimeCell,
  EmptyState
} from './styled';

// DatePicker and Select are used directly

// Format duration in human readable format
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
};

// Format phone number
const formatPhoneNumber = (phone) => {
  if (!phone) return 'Unknown';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Status labels
const STATUS_LABELS = {
  'completed': 'Answered',
  'in-progress': 'In Call',
  'ringing': 'Ringing',
  'initiated': 'Connecting',
  'no-answer': 'No Answer',
  'busy': 'Busy',
  'failed': 'Failed',
  'canceled': 'Canceled'
};

const CallLogs = observer(() => {
  // Set page header
  const { setPageHeader } = usePageHeader();
  
  useEffect(() => {
    setPageHeader({
      title: 'Call Logs',
      showDialer: true
    });
  }, [setPageHeader]);

  // State
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    direction: null,
    status: null,
    dateRange: null,
    hasRecording: null
  });
  const [activeTab, setActiveTab] = useState('completed');
  
  // Selected rows for bulk actions
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  
  // Drawer and Modal state
  const [selectedCall, setSelectedCall] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playingCall, setPlayingCall] = useState(null);

  // Fetch calls
  const fetchCalls = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        direction: filters.direction || undefined,
        status: activeTab === 'completed' ? 'completed' : 
                activeTab === 'active' ? 'in-progress,ringing,initiated' :
                activeTab === 'missed' ? 'no-answer,busy' :
                filters.status || undefined,
        dateFrom: filters.dateRange?.[0]?.toISOString() || undefined,
        dateTo: filters.dateRange?.[1]?.toISOString() || undefined
      };
      
      const response = await VoiceCallService.getVoiceCalls(params);
      
      if (response.success) {
        const callsData = response.calls || response.data || [];
        setCalls(Array.isArray(callsData) ? callsData : []);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.total || response.pagination?.total || 0
        }));
      } else {
        setCalls([]);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
      message.error('Failed to load call logs');
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, activeTab]);

  // Initial load
  useEffect(() => {
    fetchCalls(1);
  }, [fetchCalls]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle row click
  const handleRowClick = (record) => {
    setSelectedCall(record);
    setDrawerOpen(true);
  };

  // Handle play recording
  const handlePlayRecording = (e, record) => {
    e.stopPropagation();
    if (record.recording?.s3Key || record.recording?.url) {
      setPlayingCall(record);
      setPlayerOpen(true);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Recording',
      dataIndex: 'recording',
      key: 'recording',
      width: 100,
      render: (recording, record) => {
        const hasRecording = recording?.s3Key || recording?.url;
        return (
          <RecordingCell 
            $hasRecording={hasRecording}
            onClick={(e) => hasRecording && handlePlayRecording(e, record)}
          >
            <div className="waveform">
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
              <div className="bar" />
            </div>
            <Tooltip title={hasRecording ? 'Play Recording' : 'No Recording'}>
              <div className="play-icon">
                <FontAwesomeIcon icon={faPlay} />
              </div>
            </Tooltip>
          </RecordingCell>
        );
      }
    },
    {
      title: 'Call ID',
      dataIndex: 'callSid',
      key: 'callSid',
      width: 140,
      render: (callSid) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#8c8c8c' }}>
          {callSid?.slice(0, 10)}...
        </span>
      )
    },
    {
      title: 'In/Out',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
      render: (direction) => (
        <DirectionCell $direction={direction}>
          <div className="direction-icon">
            <FontAwesomeIcon icon={direction === 'inbound' ? faArrowDown : faArrowUp} />
          </div>
        </DirectionCell>
      )
    },
    {
      title: 'To',
      dataIndex: 'phoneNumber',
      key: 'to',
      width: 160,
      render: (phone, record) => (
        <PhoneNumberCell>
          <div className="phone-number">{formatPhoneNumber(phone)}</div>
          {record.patientName && (
            <div className="contact-name">{record.patientName}</div>
          )}
        </PhoneNumberCell>
      )
    },
    {
      title: 'From',
      dataIndex: 'fromNumber',
      key: 'from',
      width: 160,
      render: (phone) => (
        <PhoneNumberCell>
          <div className="phone-number">{formatPhoneNumber(phone) || '(864) 385-1918'}</div>
        </PhoneNumberCell>
      )
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      sorter: (a, b) => (a.duration || 0) - (b.duration || 0),
      render: (duration) => (
        <DurationBadge $duration={duration}>
          {formatDuration(duration)}
        </DurationBadge>
      )
    },
    {
      title: 'Created',
      dataIndex: 'initiatedAt',
      key: 'created',
      width: 160,
      sorter: (a, b) => new Date(a.initiatedAt) - new Date(b.initiatedAt),
      defaultSortOrder: 'descend',
      render: (date) => (
        <DateTimeCell>
          <div className="date">{moment(date).format('M/D/YYYY')}</div>
          <div className="time">{moment(date).format('h:mm A')}</div>
        </DateTimeCell>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <StatusBadge $status={status}>
          {STATUS_LABELS[status] || status}
        </StatusBadge>
      )
    }
  ];

  // Quick filter buttons
  const quickFilters = [
    { key: 'today', label: 'Today', icon: null },
    { key: 'week', label: 'Week', icon: null },
    { key: 'month', label: 'Month', icon: null }
  ];

  // Handle quick date filter
  const handleQuickDateFilter = (key) => {
    let range;
    switch (key) {
      case 'today':
        range = [moment().startOf('day'), moment().endOf('day')];
        break;
      case 'week':
        range = [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')];
        break;
      case 'month':
        range = [moment().subtract(30, 'days').startOf('day'), moment().endOf('day')];
        break;
      default:
        range = null;
    }
    setFilters(prev => ({ ...prev, dateRange: range }));
  };

  return (
    <PageContainer>
      {/* Filters Card */}
      <FiltersCard>
        <FiltersBar>
          <QuickFilterGroup>
            {quickFilters.map(f => (
              <Button
                key={f.key}
                type={filters.dateRange && f.key === 'today' ? 'primary' : 'default'}
                size="small"
                icon={<ClockCircleOutlined />}
                onClick={() => handleQuickDateFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </QuickFilterGroup>
          
          <div className="filter-divider" />
          
          <QuickFilterGroup>
            <Button
              type={activeTab === 'missed' ? 'primary' : 'default'}
              size="small"
              danger={activeTab === 'missed'}
              icon={<FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: 4 }} />}
              onClick={() => setActiveTab(activeTab === 'missed' ? 'completed' : 'missed')}
            >
              Missed
            </Button>
            <Button
              type={activeTab === 'completed' ? 'primary' : 'default'}
              size="small"
              icon={<FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 4 }} />}
              onClick={() => setActiveTab('completed')}
            >
              Answered
            </Button>
            <Button
              type={filters.hasRecording ? 'primary' : 'default'}
              size="small"
              icon={<FontAwesomeIcon icon={faBolt} style={{ marginRight: 4 }} />}
              onClick={() => handleFilterChange('hasRecording', !filters.hasRecording)}
            >
              AI Handled
            </Button>
          </QuickFilterGroup>
          
          <div style={{ flex: 1 }} />
          
          <Button
            className="icon-btn"
            icon={<ReloadOutlined />}
            onClick={() => fetchCalls(pagination.current)}
          />
          
          <Button
            icon={<FilterOutlined />}
          >
            Filters
          </Button>
        </FiltersBar>
      </FiltersCard>

      <PageContent>
        {/* Table */}
        <TableContainer>
          <Table
            columns={columns}
            dataSource={calls}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} calls`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={(pag) => {
              setPagination(prev => ({ ...prev, ...pag }));
              fetchCalls(pag.current);
            }}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys
            }}
            locale={{
              emptyText: (
                <EmptyState>
                    <div className="empty-icon">
                      <PhoneOutlined />
                    </div>
                    <div className="empty-title">No Call History</div>
                    <div className="empty-description">
                      Call logs will appear here once calls are made.
                    </div>
                  </EmptyState>
                )
              }}
            />
          </TableContainer>
      </PageContent>

      {/* Call Details Drawer */}
      <CallDetailsDrawer
        open={drawerOpen}
        call={selectedCall}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCall(null);
        }}
        onPlayRecording={() => {
          setPlayingCall(selectedCall);
          setPlayerOpen(true);
        }}
      />

      {/* Recording Player Modal */}
      <RecordingPlayerModal
        open={playerOpen}
        call={playingCall}
        onClose={() => {
          setPlayerOpen(false);
          setPlayingCall(null);
        }}
      />
    </PageContainer>
  );
});

export default CallLogs;
